import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder, 
  name,
  id,
  required = false 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from multiple sources (GéoPF + OpenStreetMap + Google Places)
  const fetchSuggestions = async (searchText) => {
    if (searchText.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    try {
      // Requête 1 : Points d'intérêt publics GeoPF (gares, aéroports, etc.)
      const poiPromise = fetch(
        `https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(searchText)}&type=PositionOfInterest&maximumResponses=3`
      );

      // Requête 2 : Adresses de rues GeoPF
      const addressPromise = fetch(
        `https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(searchText)}&type=StreetAddress&maximumResponses=3`
      );

      // Requête 3 : POI commerciaux OpenStreetMap (TOUS types de lieux, sans filtre)
      const osmPromise = fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchText)}&format=json&addressdetails=1&limit=5&countrycodes=fr`,
        {
          headers: {
            'User-Agent': 'LadyGoVTC/1.0 (Contact form autocomplete)'
          }
        }
      );

      // Requête 4 : Google Places API (tous commerces et lieux)
      const googlePlacesPromise = new Promise((resolve) => {
        if (!window.google?.maps?.places) {
          resolve([]);
          return;
        }
        
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          {
            input: searchText,
            componentRestrictions: { country: 'fr' },
            types: ['establishment', 'geocode']
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else {
              resolve([]);
            }
          }
        );
      });

      const [poiResponse, addressResponse, osmResponse, googlePredictions] = await Promise.all([
        poiPromise, 
        addressPromise, 
        osmPromise,
        googlePlacesPromise
      ]);
      
      const poiData = await poiResponse.json();
      const addressData = await addressResponse.json();
      const osmData = await osmResponse.json();
      
      // Traiter les résultats GeoPF
      const poiResults = (poiData.results || []).map(item => ({ 
        ...item, 
        source: 'geopf',
        category: 'poi',
        isPOI: true 
      }));
      
      const addressResults = (addressData.results || []).map(item => ({ 
        ...item, 
        source: 'geopf',
        category: 'address',
        isPOI: false 
      }));
      
      // Traiter les résultats OpenStreetMap (TOUS types, sans filtre)
      const osmResults = osmData
        .map(item => {
          const displayName = item.display_name || '';
          const nameParts = displayName.split(',');
          const mainName = nameParts[0] || displayName;
          
          // Extraire l'adresse complète
          const address = item.address || {};
          const houseNumber = address.house_number || '';
          const road = address.road || address.street || address.pedestrian || '';
          const city = address.city || address.town || address.village || '';
          const zipcode = address.postcode || '';
          
          // Construire l'adresse de rue complète
          let streetAddress = '';
          if (houseNumber && road) {
            streetAddress = `${houseNumber} ${road}`;
          } else if (road) {
            streetAddress = road;
          }
          
          // Déterminer le type de POI automatiquement
          let poiCategory = 'commerce';
          const type = item.type || '';
          const osmClass = item.class || '';
          
          // Catégorisation intelligente basée sur le type OSM
          if (['restaurant', 'fast_food', 'food_court'].includes(type)) poiCategory = 'restaurant';
          else if (['hotel', 'motel', 'hostel', 'guest_house', 'apartment'].includes(type)) poiCategory = 'hotel';
          else if (['cafe', 'bar', 'pub', 'nightclub', 'biergarten'].includes(type)) poiCategory = 'bar';
          else if (osmClass === 'shop' || ['shop', 'supermarket', 'mall', 'department_store'].includes(type)) poiCategory = 'magasin';
          else if (['tourism', 'museum', 'gallery', 'attraction', 'cinema', 'theatre', 'arts_centre', 'viewpoint', 'artwork'].includes(osmClass) || 
                   ['museum', 'attraction', 'viewpoint'].includes(type)) poiCategory = 'tourisme';
          else if (['station', 'airport', 'train_station', 'bus_station'].includes(type)) poiCategory = 'poi';
          
          return {
            fulltext: mainName,
            street: streetAddress,
            city: city,
            zipcode: zipcode,
            x: parseFloat(item.lon),
            y: parseFloat(item.lat),
            source: 'osm',
            category: poiCategory,
            isPOI: true,
            osmType: type,
            osmClass: osmClass
          };
        });
      
      // Traiter les résultats Google Places
      const googleResults = googlePredictions.map(prediction => {
        // Extraire le nom principal et l'adresse
        const description = prediction.description || '';
        const parts = description.split(',');
        const mainName = parts[0] || description;
        const address = parts.slice(1).join(',').trim();
        
        // Déterminer la catégorie basée sur les types Google
        let category = 'commerce';
        const types = prediction.types || [];
        
        if (types.includes('restaurant') || types.includes('food')) category = 'restaurant';
        else if (types.includes('lodging') || types.includes('hotel')) category = 'hotel';
        else if (types.includes('bar') || types.includes('cafe') || types.includes('night_club')) category = 'bar';
        else if (types.includes('store') || types.includes('shopping_mall')) category = 'magasin';
        else if (types.includes('tourist_attraction') || types.includes('museum')) category = 'tourisme';
        else if (types.includes('train_station') || types.includes('airport')) category = 'poi';
        
        return {
          fulltext: mainName,
          street: address,
          city: '',
          zipcode: '',
          placeId: prediction.place_id,
          source: 'google',
          category: category,
          isPOI: true,
          googlePrediction: prediction
        };
      });
      
      // Combiner : POI publics, Google Places, OSM, puis adresses
      // Google Places en priorité car base la plus complète
      const combined = [...poiResults, ...googleResults, ...osmResults, ...addressResults].slice(0, 8);
      
      if (combined.length > 0) {
        setSuggestions(combined);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (value) {
        fetchSuggestions(value);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [value]);

  const handleInputChange = (e) => {
    onChange(e);
    setSelectedIndex(-1);
  };

  const handleSelectSuggestion = async (suggestion) => {
    // Si c'est un résultat Google Places, on doit récupérer les coordonnées
    if (suggestion.source === 'google' && suggestion.placeId) {
      if (!window.google?.maps?.places) {
        console.error('Google Places API not loaded');
        return;
      }
      
      // Créer un service pour récupérer les détails du lieu
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      service.getDetails(
        {
          placeId: suggestion.placeId,
          fields: ['geometry', 'formatted_address', 'name', 'address_components']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const coords = {
              x: place.geometry.location.lng(),
              y: place.geometry.location.lat()
            };
            const fullAddress = place.name || place.formatted_address || suggestion.fulltext;
            onSelect(fullAddress, coords);
            setShowSuggestions(false);
            setSuggestions([]);
          } else {
            console.error('Failed to get place details:', status);
          }
        }
      );
    } else {
      // Pour GeoPF et OSM, les coordonnées sont déjà disponibles
      const fullAddress = suggestion.fulltext || suggestion.street || '';
      const coords = { x: suggestion.x, y: suggestion.y };
      onSelect(fullAddress, coords);
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => value && suggestions.length > 0 && setShowSuggestions(true)}
          required={required}
          className="w-full px-4 py-3 rounded-lg bg-black/50 border border-rose-900/30 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-200/50 focus:border-transparent transition-all pr-10"
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="w-5 h-5 text-rose-200 animate-spin" />
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-[#1a1014] border border-rose-900/30 rounded-lg shadow-2xl max-h-72 overflow-y-auto">
          {suggestions.map((suggestion, index) => {
            const fulltext = suggestion.fulltext || '';
            const street = suggestion.street || '';
            const city = suggestion.city || '';
            const zipcode = suggestion.zipcode || '';
            const category = suggestion.category || '';
            const source = suggestion.source || '';
            
            // Définir le badge selon la catégorie
            let badge = null;
            if (category === 'poi') {
              badge = { text: 'GARE/AÉROPORT', color: 'bg-blue-900/40 text-blue-200 border-blue-700/30' };
            } else if (category === 'restaurant') {
              badge = { text: 'RESTAURANT', color: 'bg-orange-900/40 text-orange-200 border-orange-700/30' };
            } else if (category === 'hotel') {
              badge = { text: 'HÔTEL', color: 'bg-purple-900/40 text-purple-200 border-purple-700/30' };
            } else if (category === 'bar') {
              badge = { text: 'BAR/CAFÉ', color: 'bg-amber-900/40 text-amber-200 border-amber-700/30' };
            } else if (category === 'magasin') {
              badge = { text: 'MAGASIN', color: 'bg-green-900/40 text-green-200 border-green-700/30' };
            } else if (category === 'tourisme') {
              badge = { text: 'TOURISME', color: 'bg-pink-900/40 text-pink-200 border-pink-700/30' };
            } else if (category === 'commerce') {
              badge = { text: 'COMMERCE', color: 'bg-teal-900/40 text-teal-200 border-teal-700/30' };
            }
            
            // Badge spécial pour Google Places (source premium)
            const isGoogle = source === 'google';

            return (
              <button
                key={`${suggestion.x || suggestion.placeId}-${suggestion.y}-${index}`}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className={`w-full text-left px-4 py-3 flex items-start space-x-3 transition-colors ${
                  index === selectedIndex
                    ? 'bg-rose-200/20 text-white'
                    : 'text-white hover:bg-rose-900/20'
                } ${index > 0 ? 'border-t border-rose-900/20' : ''}`}
              >
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0 text-rose-300" />
                <div className="flex-1 min-w-0">
                  {/* Nom du lieu - plus grand et blanc */}
                  <p className="text-base font-medium text-white">{fulltext}</p>
                  
                  {/* Adresse de rue si disponible */}
                  {street && (
                    <p className="text-sm text-gray-300 mt-1">
                      {street}
                    </p>
                  )}
                  
                  {/* Ville et code postal */}
                  {city && (
                    <p className="text-sm text-gray-400 mt-0.5">
                      {zipcode && `${zipcode} `}{city}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && !isLoading && value.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-2 bg-[#1a1014] border border-rose-900/30 rounded-lg shadow-2xl px-4 py-3">
          <p className="text-sm text-gray-400 text-center">Aucune adresse trouvée</p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
